// src/components/billing/ExpertBillingCard.jsx
// Version mise à jour compatible avec le nouveau système de facturation
import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { ProfileContext } from "@/context/ProfileContext";
import { useTranslation } from "react-i18next";
import { DollarSign, TrendingUp, AlertCircle, CheckCircle, Clock } from "lucide-react";

export default function ExpertBillingCard() {
  const { profile } = useContext(ProfileContext);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [iban, setIban] = useState(profile?.iban || "");
  const [bic, setBic] = useState(profile?.bic || "");
  const [saving, setSaving] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState({
    pendingAmount: 0,
    totalEarned: 0,
    nextPayout: null,
  });

  const locale = i18n.resolvedLanguage || i18n.language || "fr";

  const loadPayouts = async () => {
    const { data } = await supabase
      .from("expert_payouts")
      .select("id, amount_cents, currency, status, scheduled_for, paid_at, meta")
      .eq("expert_id", profile.id)
      .order("scheduled_for", { ascending: false });

    setPayouts(data || []);

    if (data) {
      const pending = data
        .filter((p) => p.status === "scheduled")
        .reduce((sum, p) => sum + (p.amount_cents || 0), 0);

      const earned = data
        .filter((p) => p.status === "paid")
        .reduce((sum, p) => sum + (p.amount_cents || 0), 0);

      const next = data.find((p) => p.status === "scheduled");

      setStats({
        pendingAmount: pending,
        totalEarned: earned,
        nextPayout: next,
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (profile?.id) {
      loadPayouts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          iban,
          bic,
          updated_at: new Date().toISOString(),
        })
        .eq("id", profile.id);

      if (error) throw error;
    } catch (err) {
      console.error("Error saving IBAN:", err);
    } finally {
      setSaving(false);
    }
  };

  const formatEUR = (cents) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
    }).format((cents || 0) / 100);

  const formatDate = (value) =>
    value ? new Intl.DateTimeFormat(locale).format(new Date(value)) : "";

  const formatStatus = (p) => {
    if (p.status === "paid" && p.paid_at) {
      return {
        text: t("expertBilling.status.paidOn", { date: formatDate(p.paid_at) }),
        color: "text-green-600",
        icon: CheckCircle,
      };
    }

    if (p.status === "scheduled" && p.scheduled_for) {
      return {
        text: t("expertBilling.status.scheduledFor", {
          date: formatDate(p.scheduled_for),
        }),
        color: "text-amber-600",
        icon: Clock,
      };
    }

    return {
      text: t(`expertBilling.status.values.${p.status}`, {
        defaultValue: p.status || t("expertBilling.status.unknown"),
      }),
      color: "text-gray-600",
      icon: AlertCircle,
    };
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">{t("expertBilling.stats.totalEarned")}</p>
            <TrendingUp size={16} className="text-green-600" />
          </div>
          <p className="text-2xl font-semibold" style={{ fontFamily: "Georgia, serif" }}>
            {formatEUR(stats.totalEarned)}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">{t("expertBilling.stats.pending")}</p>
            <Clock size={16} className="text-amber-600" />
          </div>
          <p className="text-2xl font-semibold" style={{ fontFamily: "Georgia, serif" }}>
            {formatEUR(stats.pendingAmount)}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">{t("expertBilling.stats.nextPayout")}</p>
            <DollarSign size={16} className="text-blue-600" />
          </div>
          <p className="text-lg font-semibold" style={{ fontFamily: "Georgia, serif" }}>
            {stats.nextPayout
              ? formatEUR(stats.nextPayout.amount_cents)
              : t("expertBilling.stats.none")}
          </p>
          {stats.nextPayout && (
            <p className="text-xs text-gray-500 mt-1">
              {formatDate(stats.nextPayout.scheduled_for)}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium mb-4" style={{ fontFamily: "Georgia, serif" }}>
          {t("expertBilling.bank.title")}
        </h3>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">IBAN</label>
            <input
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="FR76 1234 5678 9012 3456 7890 123"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">BIC/SWIFT</label>
            <input
              value={bic}
              onChange={(e) => setBic(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="ABCDEFGHXXX"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 font-medium"
          >
            {saving ? t("expertBilling.bank.saving") : t("expertBilling.bank.save")}
          </button>
        </form>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex gap-2">
            <AlertCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-blue-900">
              <p className="font-medium">{t("expertBilling.security.title")}</p>
              <p className="mt-1">{t("expertBilling.security.description")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium mb-4" style={{ fontFamily: "Georgia, serif" }}>
          {t("expertBilling.history.title")}
        </h3>
        <div className="space-y-2">
          {payouts.map((p) => {
            const status = formatStatus(p);
            const StatusIcon = status.icon;

            return (
              <div
                key={p.id}
                className="border rounded-lg p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium" style={{ fontFamily: "Georgia, serif" }}>
                      {formatEUR(p.amount_cents)}
                    </span>
                    <span className="text-sm text-gray-500">{p.currency || "EUR"}</span>
                  </div>
                  <div className={`text-sm flex items-center gap-1 ${status.color}`}>
                    <StatusIcon size={14} />
                    <span>{status.text}</span>
                  </div>
                </div>
                {p.meta?.ticket_id && (
                  <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {t("expertBilling.history.ticket", {
                      id: p.meta.ticket_id.slice(0, 8),
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {payouts.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Clock size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t("expertBilling.history.emptyTitle")}</p>
              <p className="text-xs mt-1">{t("expertBilling.history.emptyDescription")}</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0">
            <DollarSign size={20} />
          </div>
          <div className="flex-1">
            <h4 className="font-medium mb-1">{t("expertBilling.fullBilling.title")}</h4>
            <p className="text-sm text-gray-600 mb-3">
              {t("expertBilling.fullBilling.description")}
            </p>
            <button
              onClick={() => {
                if (location.pathname.includes("expert")) {
                  const event = new CustomEvent("switchExpertTab", { detail: "billing" });
                  window.dispatchEvent(event);
                } else {
                  navigate("/expert/dashboard?tab=billing");
                }
              }}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
            >
              {t("expertBilling.fullBilling.open")} →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
